
































































import { isViewAsSelected } from "@/store/modules/TargetUserModule";
import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";

import Vehicle from "@/data/models/Vehicle";
import MultiSelectionPickerComponent from "../../common/MultiSelectionPicker.vue";
import { findBranch, getBranchesOfClient } from "@/data/repository/ClientRepository";
import { getBranchName } from "@/data/models/Branch";
import UserPickerComponent from "../../common/UserPickerComponent.vue";

import {
  updateVehicle,
  deleteVehicle,
} from "@/data/repository/VehicleRepository";
import { generateRandomUuid } from "@/utils/Utils";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { getBranchesVisibleToActiveUser } from "@/data/repository/UserRepository";
import { toNumber } from "lodash";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "multi-selection-picker": MultiSelectionPickerComponent,
    "user-picker": UserPickerComponent,
    picker: PickerDialogComponent,
  },
})
export default class VehicleFormComponent extends Vue {
  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Watch("hasDetailsEdited")
  onDetailsEdited() {
    this.baseDialogComponent.neutralButtonText = this.calculateNeutralButtonText() ?? "";
    this.baseDialogComponent.negativeButtonText = this.calculateNegativeButtonText();
  }

  showingSuccessSnackbar = false;
  successSnackbarText = "";

  readonly = false;

  showSelectNothingOption = false;
  isLoading = false;

  branchesOfClient = getBranchesOfClient();

  existingVehicles: Vehicle[] = [];
  initialVehicleDetails: Vehicle | null = null;

  resolve?: (value: boolean) => void = undefined;
  reject?: (reason: string) => void = undefined;

  enableCockpit = true;
  enableCockpitOptions = [true, false];

  dataChangeDetected = false;
  isStartedForVehicleCreation = false;

  id = "";
  vehicleId = "";
  vehicleSortValue = 1;
  licencePlate = "";
  branch: string | null | undefined = null;
  capacity = 1;
  allocatedUser: string | null = null;

  licencePlateErrors: string[] = [];
  vehicleIdErrors: string[] = [];

  cancelButtonText = this.$t("common.abort").toString();
  closeButtonText = this.$t("common.close").toString();

  get vehicleIdLabel() {
    return this.withMandatoryMarker(
      this.$t("settings.vehicleManagement.vehicleId").toString(),
      true
    );
  }

  get licencePlateLabel() {
    return this.withMandatoryMarker(
      this.$t("settings.vehicleManagement.licencePlate").toString(),
      true
    );
  }

  /** Appends a required marker to a label when the field is mandatory. */
  withMandatoryMarker(label: string, isMandatory: boolean) {
    return isMandatory ? `${label} *` : label;
  }

  getBranchId(branchNumber: string | number | null | undefined) {
    if (!branchNumber) {
      return null;
    }

    const branches = getBranchesOfClient();
    const deviceBranch = branches.filter(branch => branchNumber.toString() === branch.branchNumber.toString());

    if (deviceBranch.length === 1) {
      return deviceBranch[0].id;
    }

    return null;
  }

  getBranchNumber(branchId: string | null | undefined) {
    if (!branchId) {
      return null;
    }

    const branches = getBranchesOfClient();
    const deviceBranch = branches.filter(branch => branchId === branch.id.toString());

    if (deviceBranch.length === 1) {
      return deviceBranch[0].branchNumber;
    }

    return null;
  }

  get branchOptions() {
    return getBranchesVisibleToActiveUser().map((branch) => branch.id);
  }

  get branchFormatter() {
    return (branchId: string) => {
      return getBranchName(findBranch(branchId));
    };
  }

  get hasDetailsEdited() {
    if (!this.initialVehicleDetails) {
      return false;
    }

    return this.vehicleId !== this.initialVehicleDetails.vehicleId ||
      this.vehicleSortValue !== this.initialVehicleDetails.vehicleSortValue ||
      this.licencePlate !== this.initialVehicleDetails.licencePlate ||
      this.getBranchNumber(this.branch) !== this.initialVehicleDetails.branch ||
      this.capacity !== this.initialVehicleDetails.capacity;
  }

  initializeVehicleDetails(vehicle: Vehicle) {
    this.id = vehicle.id;
    this.vehicleId = vehicle.vehicleId;
    this.vehicleSortValue = vehicle.vehicleSortValue;
    this.licencePlate = vehicle.licencePlate;
    this.branch = this.getBranchId(vehicle.branch);
    this.capacity = vehicle.capacity;
    this.allocatedUser = vehicle.allocatedUser ?? null;

    this.initialVehicleDetails = vehicle;
  }

  calculateDialogTitle() {
    return this.isStartedForVehicleCreation
      ? this.$t("settings.vehicleManagement.createVehicle").toString()
      : this.$t("settings.vehicleManagement.editVehicle").toString();
  }

  calculatePositiveButtonText() {
    return this.readonly
      ? undefined
      : this.$t("common.save").toString();
  }

  get canDeleteVehicle() {
    return !(
      this.hasDetailsEdited || 
      this.isStartedForVehicleCreation || 
      this.readonly || 
      this.allocatedUser ||
      (this.initialVehicleDetails?.devices?.length ?? 0) > 0
    );
  }

  calculateNeutralButtonText() {
    return this.canDeleteVehicle
      ? this.$t("settings.vehicleManagement.deleteVehicle").toString()
      : undefined;
  }

  calculateNegativeButtonText() {
    return this.hasDetailsEdited
      ? this.cancelButtonText
      : this.closeButtonText;
  }

  resetErrors() {
    this.licencePlateErrors = [];
    this.vehicleIdErrors = [];
  }

  async show(
    existingVehicles: Vehicle[],
    vehicle?: Vehicle,
  ) {
    this.resetErrors();

    this.existingVehicles = existingVehicles;
    this.initialVehicleDetails = vehicle ?? null;

    if (vehicle) {
      this.initializeVehicleDetails(vehicle);
    } else {
      this.initializeVehicleDetails({
        id: generateRandomUuid(),
        vehicleSortValue: 1,
        allocatedUser: null,
        vehicleId: "",
        licencePlate: "",
        branch: null,
        capacity: 1,
      })

      this.isStartedForVehicleCreation = true;
    }

    this.baseDialogComponent.show({
      title: this.calculateDialogTitle(),
      // View only while "Sicht auf" is active (ticket 2610051).
      positiveButtonText: isViewAsSelected() ? null : this.calculatePositiveButtonText(),
      negativeButtonText: this.calculateNegativeButtonText(),
      neutralButtonText: this.calculateNeutralButtonText(),

      closeOnSave: false,
      closeOnNeutralAction: false,
      persistent: true,
      maxWidth: 800,
    });

    return new Promise<boolean>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  async onDeleteVehicle() {
    if (!this.canDeleteVehicle) {
      return;
    }

    const result = await this.confirmationDialogComponent.show({
      title: this.$t("settings.vehicleManagement.deleteVehicle").toString(),
      message: this.$t("settings.vehicleManagement.deleteVehicleMessage", [
        this.vehicleId,
      ]).toString(),
    });

    if (result && this.initialVehicleDetails) {
      this.isLoading = true;

      await deleteVehicle(this.id);

      this.baseDialogComponent.isShowing = false;
      this.isLoading = false;
    }
  }

  async onSave() {
    this.isLoading = true;
    this.resetErrors();

    const vehicleId = this.vehicleId;
    const licencePlate = this.licencePlate;
    const existingVehicles = this.existingVehicles;

    if (!this.vehicleId || vehicleId === "") {
      this.vehicleIdErrors = [
        this.$t("settings.vehicleManagement.errorMissingVehicleId").toString(),
      ];

      this.isLoading = false;
      return;
    }

    if (!this.licencePlate || licencePlate === "") {
      this.licencePlateErrors = [
        this.$t("settings.vehicleManagement.errorMissingLicencePlate").toString(),
      ];

      this.isLoading = false;
      return;
    }

    const existingVehicleWithVehicleId = existingVehicles.find(
      vehicle => 
        vehicle.id !== this.id &&
        vehicle.vehicleId.toLowerCase() === vehicleId.toLowerCase()
    );

    if (existingVehicleWithVehicleId) {
      this.vehicleIdErrors = [
        this.$t("settings.vehicleManagement.errorDuplicateVehicleId").toString(),
      ];

      this.isLoading = false;
      return;
    }

    const existingVehicleWithLicencePlate = existingVehicles.find(
      vehicle => 
        vehicle.id !== this.id &&
        vehicle.licencePlate.toLowerCase() === licencePlate.toLowerCase()
    );

    if (existingVehicleWithLicencePlate) {
      this.licencePlateErrors = [
        this.$t("settings.vehicleManagement.errorDuplicateLicencePlate").toString(),
      ];

      this.isLoading = false;
      return;
    }

    const vehicleUpdate: Vehicle = {
      id: this.id,
      vehicleId: this.vehicleId,
      allocatedUser: this.allocatedUser,
      vehicleSortValue: toNumber(this.vehicleSortValue),
      licencePlate: this.licencePlate,
      branch: this.getBranchNumber(this.branch),
      capacity: this.capacity,
    };

    await updateVehicle(vehicleUpdate);
    
    this.baseDialogComponent.isShowing = false;
    this.isLoading = false;
  }

  onAbortEditVehicleDetailsConfirmed() {
    this.baseDialogComponent.isShowing = false;

    this.baseDialogComponent.negativeButtonText = this.closeButtonText;
    this.baseDialogComponent.persistent = false;

    this.initialVehicleDetails = null;
    this.isStartedForVehicleCreation = false;
  }

  async onAbortEditVehicleDetails() {
    if (this.hasDetailsEdited) {
      const result = await this.confirmationDialogComponent.show({
        title: this.$t("common.confirmationDialog.cancelTitle").toString(),
        message: this.$t("common.confirmationDialog.cancelMessage").toString(),
      });

      if (result) {
        this.onAbortEditVehicleDetailsConfirmed();
      }
    }
    else {
      this.onAbortEditVehicleDetailsConfirmed();
    }
  }
}
